/**
 * User-friendly error message utilities
 */

// User-friendly error message mapping
const ERROR_MESSAGES: Record<string, string> = {
  // Auth errors
  AUTH_REQUIRED: "Please log in to continue.",
  FORBIDDEN: "You don't have permission to perform this action.",
  PERMISSION_DENIED: "You don't have permission to perform this action.",
  REQUIRES_APPROVAL:
    "This action requires authorization from an admin or supervisor.",
  INVALID_CREDENTIALS: "Invalid email or password. Please try again.",
  USER_NOT_SELECTED: "Please select a user to continue.",

  // Validation
  VALIDATION_ERROR: "Please check your input and try again.",
  INVALID_DATE: "Please enter a valid date.",
  DATE_IN_FUTURE: "Date cannot be in the future.",
  INVALID_FORMAT: "Invalid format. Please check your input.",

  // Resources
  NOT_FOUND: "The requested item was not found.",
  CONFLICT: "This record already exists.",
  DUPLICATE_RECORD: "This record already exists in the system.",
  DUPLICATE_NPI: "This NPI number is already registered.",
  DUPLICATE_NDC: "A product with this NDC already exists in your inventory.",
  INVALID_REFERENCE:
    "Referenced record not found. Please refresh and try again.",

  // System
  INTERNAL_ERROR: "Something went wrong. Please try again or contact support.",
  NETWORK_ERROR: "Network error. Please check your connection and try again.",
  METHOD_NOT_ALLOWED: "This operation is not allowed.",
  BAD_REQUEST: "Invalid request. Please check your input.",
};

/**
 * Get a user-friendly error message from an error object
 */
export function getUserFriendlyError(
  error: { error?: string; code?: string; message?: string } | unknown,
  fallback = "An unexpected error occurred. Please try again."
): string {
  // Handle null/undefined
  if (!error) return fallback;

  const errorObj = error as {
    error?: string;
    code?: string;
    message?: string;
  };

  // If we have an error code, use the mapped message
  if (errorObj.code && ERROR_MESSAGES[errorObj.code]) {
    return ERROR_MESSAGES[errorObj.code];
  }

  // If the error message is already user-friendly (no technical terms), use it
  const errorMsg = errorObj.error || errorObj.message || "";
  if (errorMsg && isUserFriendly(errorMsg)) {
    return errorMsg;
  }

  // Otherwise use fallback
  return fallback;
}

/**
 * Check if an error message is user-friendly (doesn't contain technical terms)
 */
function isUserFriendly(message: string): boolean {
  const technicalTerms = [
    "idx_",
    "constraint",
    "violates",
    "foreign key",
    "null value",
    "column",
    "relation",
    "syntax error",
    "sqlstate",
    "postgresql",
    "database",
    "query",
    "rpc",
    "function",
    "trigger",
    "schema",
    "does not have permission",
    "role '",
  ];

  const lowerMessage = message.toLowerCase();
  return !technicalTerms.some((term) => lowerMessage.includes(term));
}

/**
 * Extract error message from various error formats
 */
export function extractErrorMessage(error: unknown): string {
  if (!error) return "Unknown error";

  if (typeof error === "string") return error;

  if (error instanceof Error) return error.message;

  const errorObj = error as {
    error?: string;
    message?: string;
    details?: string;
  };

  return (
    errorObj.error || errorObj.message || errorObj.details || "Unknown error"
  );
}
